import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import { ScrollText, ArrowLeft } from "lucide-react";
import { Footer } from "@/components/Footer";
import { Navbar } from "@/components/Navbar";

export default function Terms() {
  const { t } = useTranslation();
  const lastUpdated = "January 1, 2026";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Navigation */}
      <Navbar />

      <div className="container flex justify-between items-center h-12">
        <Link href="/">
          <a className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {t('common.back')}
          </a>
        </Link>
      </div>

      <main className="flex-1 container py-12 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6">
            <ScrollText className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-4xl font-bold mb-4">{t('footer.terms')}</h1>
          <p className="text-muted-foreground">Last updated: {lastUpdated}</p>
        </div>

        {/* Content */}
        <div className="prose prose-lg dark:prose-invert max-w-none">
          <section className="card-elegant p-8 mb-6">
            <h2 className="text-2xl font-semibold mb-4">1. Acceptance of Terms</h2>
            <p className="text-muted-foreground">
              By accessing or using Listo Market ("Service"), you agree to be bound by these Terms of Service 
              ("Terms"). If you disagree with any part of these terms, you may not access the Service.
            </p>
            <p className="text-muted-foreground mt-4">
              We reserve the right to update these terms at any time. We will notify you of any changes by 
              posting the new Terms on this page and updating the "Last updated" date.
            </p>
          </section>

          <section className="card-elegant p-8 mb-6">
            <h2 className="text-2xl font-semibold mb-4">2. Description of Service</h2>
            <p className="text-muted-foreground">
              Listo Market is an online classifieds platform that allows users to post, browse, and respond 
              to listings for goods and services. We do not participate in transactions between users and 
              are not responsible for the quality, safety, or legality of items listed.
            </p>
          </section>

          <section className="card-elegant p-8 mb-6">
            <h2 className="text-2xl font-semibold mb-4">3. User Accounts</h2>
            <p className="text-muted-foreground mb-4">
              To access certain features of the Service, you must register for an account. You agree to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the security of your password</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized use</li>
            </ul>
          </section>

          <section className="card-elegant p-8 mb-6">
            <h2 className="text-2xl font-semibold mb-4">4. User Conduct</h2>
            <p className="text-muted-foreground mb-4">
              You agree not to use the Service to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Post false, misleading, or fraudulent content</li>
              <li>Sell illegal items or services</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Spam or send unsolicited messages</li>
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on intellectual property rights</li>
              <li>Distribute malware or harmful code</li>
              <li>Attempt to access other users' accounts</li>
              <li>Post content involving human trafficking or exploitation</li>
              <li>Engage in any activity that could harm minors</li>
            </ul>
          </section>

          <section className="card-elegant p-8 mb-6">
            <h2 className="text-2xl font-semibold mb-4">5. Listing Content</h2>
            <p className="text-muted-foreground mb-4">
              By posting a listing, you represent that:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>You have the right to sell the item or offer the service</li>
              <li>The listing accurately describes the item or service</li>
              <li>The photos are accurate and belong to you</li>
              <li>You will honor the price and terms stated</li>
              <li>The item or service complies with all applicable laws</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              We reserve the right to remove any listing that violates these terms or our community guidelines.
            </p>
          </section>

          <section className="card-elegant p-8 mb-6">
            <h2 className="text-2xl font-semibold mb-4">6. Prohibited Items</h2>
            <p className="text-muted-foreground mb-4">
              The following items and services are strictly prohibited on Listo Market:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Weapons, firearms, and ammunition</li>
              <li>Drugs and drug paraphernalia</li>
              <li>Counterfeit or stolen goods</li>
              <li>Adult content and services</li>
              <li>Personal information or data</li>
              <li>Hazardous materials</li>
              <li>Live animals (except through authorized dealers)</li>
              <li>Items subject to government restrictions</li>
            </ul>
          </section>

          <section className="card-elegant p-8 mb-6">
            <h2 className="text-2xl font-semibold mb-4">7. Fees and Payments</h2>
            <p className="text-muted-foreground">
              Basic use of Listo Market is free. We may offer premium features for a fee. All fees are 
              non-refundable unless otherwise stated. We reserve the right to modify our pricing at any time 
              with reasonable notice.
            </p>
          </section>

          <section className="card-elegant p-8 mb-6">
            <h2 className="text-2xl font-semibold mb-4">8. Intellectual Property</h2>
            <p className="text-muted-foreground">
              The Service and its original content, features, and functionality are owned by Listo Market 
              and are protected by international copyright, trademark, and other intellectual property laws. 
              You may not copy, modify, or distribute our content without permission.
            </p>
          </section>

          <section className="card-elegant p-8 mb-6">
            <h2 className="text-2xl font-semibold mb-4">9. Disclaimer of Warranties</h2>
            <p className="text-muted-foreground">
              THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DO NOT GUARANTEE THAT THE 
              SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE. WE ARE NOT RESPONSIBLE FOR THE CONDUCT 
              OF USERS OR THE QUALITY OF ITEMS LISTED.
            </p>
          </section>

          <section className="card-elegant p-8 mb-6">
            <h2 className="text-2xl font-semibold mb-4">10. Limitation of Liability</h2>
            <p className="text-muted-foreground">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, LISTO MARKET SHALL NOT BE LIABLE FOR ANY INDIRECT, 
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE 
              OR ANY TRANSACTIONS CONDUCTED THROUGH THE SERVICE.
            </p>
          </section>

          <section className="card-elegant p-8 mb-6">
            <h2 className="text-2xl font-semibold mb-4">11. Termination</h2>
            <p className="text-muted-foreground">
              We may terminate or suspend your account immediately, without prior notice, for any reason, 
              including breach of these Terms. Upon termination, your right to use the Service will cease 
              immediately. All provisions that should survive termination shall survive.
            </p>
          </section>

          <section className="card-elegant p-8 mb-6">
            <h2 className="text-2xl font-semibold mb-4">12. Governing Law</h2>
            <p className="text-muted-foreground">
              These Terms shall be governed by the laws of the United States, without regard to conflict of 
              law principles. Any disputes shall be resolved in the courts located in San Francisco, California.
            </p>
          </section>

          <section className="card-elegant p-8 mb-6">
            <h2 className="text-2xl font-semibold mb-4">13. Contact Us</h2>
            <p className="text-muted-foreground">
              If you have any questions about these Terms, please contact us at:
            </p>
            <p className="text-muted-foreground mt-4">
              <strong>Email:</strong> legal@listomarket.app<br />
              <strong>Address:</strong> 123 Market Street, San Francisco, CA 94105
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
